import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import * as QRCode from 'qrcode';
// npm install qrcode
// npm install --save-dev @types/qrcode

import { TwoFactorService } from '../../services/two-factor.service';
import { ViewScopeService } from '../../services/view-scope.service';
import { AuthStateService } from '../../services/auth-state.service';

type TwoFactorViewState = 'loading' | 'disabled' | 'settingUp' | 'enabled';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, ToastModule, ConfirmDialogModule],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class ProfileComponent implements OnInit {
  state: TwoFactorViewState = 'loading';
  isBusy = false;

  qrDataUrl: string | null = null;
  secret: string | null = null;
  confirmCode = '';

  private userRecId = 0;

  constructor(
    private twoFactorService: TwoFactorService,
    private viewScopeService: ViewScopeService,
    private authStateService: AuthStateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.userRecId = this.viewScopeService.getCurrentOpertaingUserId();
    this.loadStatus();
  }

  private loadStatus(): void {
    this.state = 'loading';
    this.twoFactorService.getStatus(this.userRecId).subscribe({
      next: (res) => {
        this.state = res.Enabled ? 'enabled' : 'disabled';
      },
      error: (err) => {
        console.error('[ProfileComponent] Failed to load 2FA status:', err);
        this.state = 'disabled';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load two-factor authentication status.' });
      }
    });
  }

  startSetup(): void {
    this.isBusy = true;
    const firstName = this.authStateService.getFirstName();
    const lastName = this.authStateService.getLastName();
    const label = (firstName + ' ' + lastName).trim() || undefined;

    this.twoFactorService.setup(this.userRecId, label).subscribe({
      next: (res) => {
        this.secret = res.Secret;
        this.confirmCode = '';
        QRCode.toDataURL(res.OtpUri)
          .then((dataUrl) => {
            this.qrDataUrl = dataUrl;
            this.isBusy = false;
            this.state = 'settingUp';
          })
          .catch((err) => {
            console.error('[ProfileComponent] Failed to render QR code:', err);
            this.isBusy = false;
            this.state = 'settingUp';
          });
      },
      error: (err) => {
        console.error('[ProfileComponent] Failed to start 2FA setup:', err);
        this.isBusy = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not start two-factor setup.' });
      }
    });
  }

  confirmSetup(): void {
    if (!this.confirmCode) {
      return;
    }
    this.isBusy = true;
    this.twoFactorService.confirm(this.userRecId, this.confirmCode).subscribe({
      next: () => {
        this.isBusy = false;
        this.qrDataUrl = null;
        this.secret = null;
        this.confirmCode = '';
        this.state = 'enabled';
        this.messageService.add({ severity: 'success', summary: 'Enabled', detail: 'Two-factor authentication is now enabled.' });
      },
      error: (err) => {
        console.error('[ProfileComponent] Invalid 2FA confirmation code:', err);
        this.isBusy = false;
        this.confirmCode = '';
        this.messageService.add({ severity: 'error', summary: 'Invalid code', detail: 'The code was incorrect or has expired - try again.' });
      }
    });
  }

  cancelSetup(): void {
    this.qrDataUrl = null;
    this.secret = null;
    this.confirmCode = '';
    this.state = 'disabled';
  }

  confirmDisable(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to disable two-factor authentication?',
      header: 'Disable Two-Factor Authentication',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.disable()
    });
  }

  private disable(): void {
    this.isBusy = true;
    this.twoFactorService.disable(this.userRecId).subscribe({
      next: () => {
        this.isBusy = false;
        this.state = 'disabled';
        this.messageService.add({ severity: 'success', summary: 'Disabled', detail: 'Two-factor authentication has been disabled.' });
      },
      error: (err) => {
        console.error('[ProfileComponent] Failed to disable 2FA:', err);
        this.isBusy = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not disable two-factor authentication.' });
      }
    });
  }
}
